import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  CalculationResultStatus,
  CallidusBvsHaftpflichtUndKaskoMoeglicheSelbstbehalteResultDto,
  CallidusBvsVersicherungspraemieHaftpflichtCalcDto,
  CallidusBvsVersicherungspraemieHaftpflichtUndKaskoCalcDto,
  ProductCallidusPipelineResultDto,
  PublicCallidusBvsApiService,
  PublicCallidusBvsHaftpflichtContractCreateDto,
  PublicCallidusBvsHaftpflichtUndKaskoCalcResultDto,
  PublicCallidusBvsHaftpflichtUndKaskoContractCreateDto,
  VersicherungspraemieCalculationResultDto
} from '@backend/models';

@Injectable()
export class BvsHaftpflichtStateService {
  private readonly publicCallidusBvsApiService = inject(PublicCallidusBvsApiService);

  /**
   * Haftpflicht Prämienberechnung Ergebnis
   */
  calculateHaftpflichtPraemie(dto: CallidusBvsVersicherungspraemieHaftpflichtCalcDto): Observable<VersicherungspraemieCalculationResultDto> {
    return this.publicCallidusBvsApiService
      .apiPublicCallidusBvsContractHaftpflichtVersicherungspraemiePost(dto);
  }

  /**
   * Haftpflicht und Kasko Prämienberechnung Ergebnis
   */
  calculateHaftpflichtUndKaskoPraemie(dto: CallidusBvsVersicherungspraemieHaftpflichtUndKaskoCalcDto): Observable<VersicherungspraemieCalculationResultDto> {
    return this.publicCallidusBvsApiService
      .apiPublicCallidusBvsContractHaftpflichtUndKaskoVersicherungspraemiePost(dto)
      .pipe(map(result => this.combineHaftpflichtUndKaskoResult(result)));
  }

  /**
   * Combines the separate Haftpflicht and Kasko results into a single total premium result.
   */
  private combineHaftpflichtUndKaskoResult(result: PublicCallidusBvsHaftpflichtUndKaskoCalcResultDto): VersicherungspraemieCalculationResultDto {
    const haftpflicht = result.haftpflichtResult;
    const kasko = result.kaskoResult;

    return {
      publicCalculationResultStatus: this.worstCalculationResultStatus(
        haftpflicht?.publicCalculationResultStatus,
        kasko?.publicCalculationResultStatus
      ),
      publicCalculationResultStatusDetails: [
        ...(haftpflicht?.publicCalculationResultStatusDetails ?? []),
        ...(kasko?.publicCalculationResultStatusDetails ?? [])
      ].filter((detail, index, all) => all.findIndex(d => d.translationKey === detail.translationKey) === index),
      versicherungspraemieInEur: (haftpflicht?.versicherungspraemieInEur ?? 0) + (kasko?.versicherungspraemieInEur ?? 0)
    };
  }

  private worstCalculationResultStatus(
    a?: CalculationResultStatus,
    b?: CalculationResultStatus
  ): CalculationResultStatus | undefined {
    const rank: Record<CalculationResultStatus, number> = {
      [CalculationResultStatus.Ok]: 0,
      [CalculationResultStatus.OkWithWarning]: 1,
      [CalculationResultStatus.Error]: 2
    };
    const candidates = [a, b].filter((s): s is CalculationResultStatus => s !== undefined);
    if (candidates.length === 0) {
      return undefined;
    }
    return candidates.reduce((worst, status) => (rank[status] > rank[worst] ? status : worst));
  }

  /**
   * Onboardings
   */
  submitHaftpflichtOnboarding(dto: PublicCallidusBvsHaftpflichtContractCreateDto): Observable<ProductCallidusPipelineResultDto> {
    return this.publicCallidusBvsApiService
      .apiPublicCallidusBvsContractHaftpflichtPost(dto);
  }

  submitHaftpflichtUndKaskoOnboarding(dto: PublicCallidusBvsHaftpflichtUndKaskoContractCreateDto): Observable<ProductCallidusPipelineResultDto> {
    return this.publicCallidusBvsApiService
      .apiPublicCallidusBvsContractHaftpflichtUndKaskoPost(dto);
  }

  /**
   * Haftpflicht und Kasko mögliche Selbstbehalte
   */
  getMoeglicheSelbstbehalteHaftpflichtUndKasko(dto: CallidusBvsVersicherungspraemieHaftpflichtUndKaskoCalcDto): Observable<CallidusBvsHaftpflichtUndKaskoMoeglicheSelbstbehalteResultDto> {
    return this.publicCallidusBvsApiService
      .apiPublicCallidusBvsContractHaftpflichtUndKaskoMoeglicheSelbstbehaltePost(dto);
  }
}

